
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface MpesaTransaction {
  id: string;
  merchant_request_id: string;
  checkout_request_id: string;
  result_code: number;
  result_desc: string;
  amount?: number;
  mpesa_receipt_number?: string;
  phone_number?: string;
  transaction_date?: number;
  account_reference?: string;
  transaction_desc?: string;
  status: 'pending' | 'completed' | 'failed' | 'cancelled';
  raw_callback_data: any;
  booking_id?: number;
  created_at: string;
  updated_at: string;
}

export const useMpesaTransactions = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const {
    data: transactions = [],
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['mpesa-transactions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('mpesa_transactions')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) {
        console.error('Error fetching MPesa transactions:', error);
        throw error;
      }

      return data as MpesaTransaction[];
    },
  });

  // Real-time subscription to transaction updates
  const subscribeToTransactions = () => {
    const channel = supabase
      .channel('mpesa-transactions-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'mpesa_transactions'
        },
        (payload) => {
          console.log('MPesa transaction update:', payload);
          
          // Invalidate and refetch the transactions
          queryClient.invalidateQueries({ queryKey: ['mpesa-transactions'] });
          
          // Show toast notification for new transactions
          if (payload.eventType === 'INSERT') {
            const transaction = payload.new as MpesaTransaction;
            toast({
              title: "New MPesa Transaction",
              description: `Transaction ${transaction.status}: ${transaction.result_desc}`,
              variant: transaction.status === 'completed' ? "success" : 
                      transaction.status === 'failed' ? "destructive" : "default",
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  // Test callback function for development
  const testCallback = useMutation({
    mutationFn: async (testData: {
      checkoutRequestId: string;
      merchantRequestId: string;
      resultCode: number;
      amount?: number;
      phoneNumber?: string;
    }) => {
      console.log('Testing MPesa callback with data:', testData);
      
      const testCallbackData = {
        stkCallback: {
          MerchantRequestID: testData.merchantRequestId,
          CheckoutRequestID: testData.checkoutRequestId,
          ResultCode: testData.resultCode,
          ResultDesc: testData.resultCode === 0 ? 
            "The service request is processed successfully." : 
            "Request failed",
          ...(testData.resultCode === 0 && {
            CallbackMetadata: {
              Item: [
                {
                  Name: "Amount",
                  Value: testData.amount || 1.00
                },
                {
                  Name: "MpesaReceiptNumber",
                  Value: `TEST${Date.now()}`
                },
                {
                  Name: "TransactionDate",
                  Value: parseInt(new Date().toISOString().replace(/[-T:]/g, '').slice(0, 14))
                },
                {
                  Name: "PhoneNumber",
                  Value: testData.phoneNumber || "254700230965"
                }
              ]
            }
          })
        }
      };

      const { data, error } = await supabase.functions.invoke('mpesa-callback', {
        body: testCallbackData
      });

      if (error) {
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      toast({
        title: "Test Callback Sent",
        description: "MPesa callback test completed successfully",
        variant: "success",
      });
      refetch();
    },
    onError: (error) => {
      console.error('Test callback error:', error);
      toast({
        title: "Test Failed",
        description: `Callback test failed: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  return {
    transactions,
    isLoading,
    error,
    refetch,
    subscribeToTransactions,
    testCallback
  };
};
